import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, SxProps, Theme } from '@mui/material';

import { SCUIZoomableImage } from '../../../atomic/atoms';

const IMAGE_LOAD_MAX_ATTEMPTS = 10;
const IMAGE_LOAD_RETRY_DELAY_MS = 1000;

export type PreviewUrlPair = {
    thumbnailSrc: string;
    originalSrc: string;
};

type RetryableZoomableImageProps = {
    imageKey: string;
    thumbnailSrc?: string;
    originalSrc?: string;
    alt?: string;
    containerSx?: SxProps<Theme>;
    imageSx?: SxProps<Theme>;
    onPreview?: (url: string) => void;
    onRetryRequest?: () => void | Promise<void | PreviewUrlPair>;
};

const uniqueUrls = (...urls: Array<string | undefined>) => {
    const seen = new Set<string>();
    const result: string[] = [];
    urls.forEach((url) => {
        const value = (url ?? '').trim();
        if (!value || seen.has(value)) {
            return;
        }
        seen.add(value);
        result.push(value);
    });
    return result;
};

const probeUrl = (url: string) =>
    new Promise<boolean>((resolve) => {
        if (!url) {
            resolve(false);
            return;
        }

        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = url;
    });

const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
    });

export const RetryableZoomableImage = ({
    imageKey,
    thumbnailSrc = '',
    originalSrc = '',
    alt = '',
    containerSx,
    imageSx,
    onPreview,
    onRetryRequest,
}: RetryableZoomableImageProps) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState<'loading' | 'ready' | 'failed'>('loading');
    const [displaySrc, setDisplaySrc] = useState('');

    const thumbnailSrcRef = useRef(thumbnailSrc);
    const originalSrcRef = useRef(originalSrc);
    const onRetryRequestRef = useRef(onRetryRequest);

    thumbnailSrcRef.current = thumbnailSrc;
    originalSrcRef.current = originalSrc;
    onRetryRequestRef.current = onRetryRequest;

    useEffect(() => {
        let cancelled = false;

        const resolveUrls = async (forceRefresh: boolean): Promise<PreviewUrlPair> => {
            let thumbnail = thumbnailSrcRef.current;
            let original = originalSrcRef.current;

            if (forceRefresh || (!thumbnail && !original)) {
                const refreshed = await Promise.resolve(onRetryRequestRef.current?.());
                if (refreshed && typeof refreshed === 'object') {
                    thumbnail = refreshed.thumbnailSrc || thumbnail;
                    original = refreshed.originalSrc || original;
                } else {
                    thumbnail = thumbnailSrcRef.current || thumbnail;
                    original = originalSrcRef.current || original;
                }
            }

            return {
                thumbnailSrc: thumbnail || '',
                originalSrc: original || '',
            };
        };

        const run = async () => {
            setPhase('loading');
            setDisplaySrc('');

            let lastThumb = '';
            let lastOriginal = '';

            for (let attempt = 0; attempt < IMAGE_LOAD_MAX_ATTEMPTS; attempt += 1) {
                if (cancelled) {
                    return;
                }

                if (attempt > 0) {
                    await sleep(IMAGE_LOAD_RETRY_DELAY_MS);
                    if (cancelled) {
                        return;
                    }
                }

                const urls = await resolveUrls(attempt > 0);
                if (cancelled) {
                    return;
                }

                lastThumb = urls.thumbnailSrc;
                lastOriginal = urls.originalSrc;

                const candidates = uniqueUrls(urls.thumbnailSrc, urls.originalSrc);
                for (const candidate of candidates) {
                    const loaded = await probeUrl(candidate);
                    if (cancelled) {
                        return;
                    }
                    if (loaded) {
                        setDisplaySrc(candidate);
                        setPhase('ready');
                        return;
                    }
                }
            }

            if (cancelled) {
                return;
            }

            setDisplaySrc(lastThumb || lastOriginal || '');
            setPhase('failed');
        };

        void run();

        return () => {
            cancelled = true;
        };
    }, [imageKey]);

    const showSpinner = phase === 'loading';

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <SCUIZoomableImage
                key={`${imageKey}-${displaySrc || 'empty'}-${phase}`}
                src={displaySrc}
                previewSrc={originalSrc || displaySrc}
                alt={alt}
                onPreview={onPreview}
                containerSx={containerSx}
                imageSx={{
                    ...imageSx,
                    opacity: showSpinner && displaySrc ? 0.35 : 1,
                    transition: 'opacity 0.15s ease',
                }}
            />
            {showSpinner && (
                <Box
                    aria-label={t('studio.rawImageFrameControl.loadingPhotos')}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress size={28} thickness={4} />
                </Box>
            )}
        </Box>
    );
};
